import fields from '@/sui/data/sheets/edit/fields'
import units from '@/sui/data/units'
import patient from '@/sui/data/sheets/edit/patient'
import tools from '@/sui/data/sheets/edit/tools'

import micro from '@/sui/data/microOrgs'

const firstLetters = `Taper la ou les premières lettres de la resistance`

const select = (function () {
  const entero = tools.inList(fields.microOrgCode, micro.enterobacteria)
  const pyo = tools.equals(fields.microOrgCode, 'PSE AER')
  const cap = tools.allTrue(
    tools.equals(fields.microOrgCode, 'STA CAP'),
    patient.select.isNeoOrNeoMix
  )
  const gly = tools.inList(fields.microOrgCode, ['STA AUR', 'ENC FAE', 'ENC FAI'])
  const aur = tools.equals(fields.microOrgCode, 'STA AUR')

  return {
    cap,
    aur,
    capAur: tools.oneTrue(cap, aur),
    capGly: tools.oneTrue(cap, gly),
    pyo,
    gly,
    entero,
    pan: tools.oneTrue(
      entero,
      tools.inList(
        fields.microOrgCode,
        ['STA AUR', 'ENC FAE', 'ENC FAI', 'PSE AER', 'ACI BAU']
      )
    ),
    flu: tools.inList(fields.microOrgCode, micro.candida),
    car: tools.oneTrue(
      entero,
      tools.inList(fields.microOrgCode, ['PSE AER', 'ACI BAU'])
    ),
    epc: tools.allTrue(entero, tools.equals(fields.epc, 'O')),
    ppc: tools.allTrue(pyo, tools.equals(fields.ppc, 'O')),
    blse: tools.allTrue(entero, tools.inList(fields.c3g, ['I', 'R', 'SFP'])),
    entCar: tools.allTrue(entero, tools.inList(fields.car, ['I', 'R', 'SFP'])),
    pyoCar: tools.allTrue(pyo, tools.inList(fields.car, ['I', 'R', 'SFP'])),
    isMoOther: tools.inList(fields.microOrgCode, ['STA AUT', 'ETB AUT'])
  }
})()

const code = (function () {
  function build (values) {
    const list = [{ value: `Aucun`, code: '' }].concat(values)
    return tools.cfg.list(
      fields.microOrgCode, 'Micro-organisme', list, {
        tt: `Taper quelques lettres pour rechercher le micro-organisme
par son code ou par son nom : ${tools.key('sta aur')} ou directement
${tools.key('aure')} pour <i>Staphylococcus aureus</i>,
${tools.key('esc')} ou ${tools.key('col')} pour
<i>Escherichia coli</i>, etc.`,
        ph: `Taper quelques lettres du code ou du nom...`,
        def: 0,
        pop: (e) => e.code == null || e.code === '',
        cm: (vm, e, target) => {
          if (e !== '') {
            vm.$set(target, fields.microOrgCode, e)
          }
        }
      }
    )
  }

  return {
    v1: {
      all: build(micro.completeList.v1),
      noVirus: build(micro.list.v1)
    },

    v2: {
      all: build(micro.completeList.v2),
      noVirus: build(micro.list.v2)
    }
  }
})()

const oxa = (function () {
  function build (v, sel) {
    return tools.cfg.list(
      fields.oxa, 'Résistance à la méticilline (OXA)', micro.oxaList[v], {
        tt: `${firstLetters}: ${tools.key('s')} pour sensible,
${tools.key('r')} résistant, ${tools.key('n')} pour non
connu...`,
        ph: '«s» pour sensible, «r» pour résistant...',
        ms: true,
        sel: sel
      }
    )
  }

  return {
    v1: build('v1', select.aur),
    v2: build('v1', select.capAur),
    v3: build('v2', select.capAur)
  }
})()

const sirnTooltip = `${firstLetters} : ${tools.key('s')} pour sensible,
${tools.key('i')} pour intermédiaire, ${tools.key('r')} pour résistant,
${tools.key('n')} pour non connu.`

const sirnPlaceholder = '«s» pour sensible, «i» pour intermédiaire...'

const ssfprnTooltip = `${firstLetters} : ${tools.key('s')} pour sensible,
ajouter ${tools.key('f')} pour sensible à forte posologie, ${tools.key('r')} pour résistant,
${tools.key('n')} pour non connu.`

const ssfprnPlaceholder = '«s» pour sensible, «r» pour résistant...'

const gly = (function () {
  function build (v, sel, tt, ph) {
    return tools.cfg.list(
      fields.gly, 'Résistance aux glycopeptides (GLY)', micro.glyList[v], {
        tt: sirnTooltip, ph: sirnPlaceholder, ms: true, sel: sel
      }
    )
  }

  return {
    v1: build('v1', select.gly, sirnTooltip, sirnPlaceholder),
    v2: build('v1', select.capGly, sirnTooltip, sirnPlaceholder),
    v3: build('v2', select.capGly, ssfprnTooltip, ssfprnPlaceholder)
  }
})()

const c3g = (function () {
  function build (v, tt, ph) {
    return tools.cfg.list(
      fields.c3g, 'Résistance aux céphalosporines de 3ème génération (C3G)',
      micro.c3gList[v], {
        tt: tt, ph: ph, ms: true, sel: select.entero
      }
    )
  }

  return {
    v1: build('v1', sirnTooltip, sirnPlaceholder),
    v2: build('v2', ssfprnTooltip, ssfprnPlaceholder)
  }
})()

const flu = tools.cfg.list(
  fields.flu, 'Résistance au fluconazole (FLU)', micro.fluList, {
    tt: `${firstLetters} : ${tools.key('s')} pour sensible, ${tools.key('sd')}
pour sensibilité dose dépendante ou résistant, ${tools.key('n')} pour non
connu.`,
    ph: '«s » pour sensible, «sd» pour dose dépendante...',
    ms: true,
    sel: select.flu
  }
)

const pan = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.pan, 'Résistance PAN', micro.panList[v], {
        tt: `${firstLetters} : ${tools.key('n')} pour sensible, ${tools.key('p')}
pour intermédiaire ou résistant, etc.`,
        ph: '«n » pour sensible, «p» pour intermédiaire...',
        ms: true,
        sel: select.pan
      }
    )
  }

  return {
    v1: build('v1'),
    v2: build('v2')
  }
})()

const car = (function () {
  function build (v, tt, ph) {
    return tools.cfg.list(
      fields.car, 'Résistance aux carbapénèmes (CARB)', micro.carList[v], {
        tt: tt, ph: ph, ms: true, sel: select.car
      }
    )
  }

  return {
    v1: build('v1', sirnTooltip, sirnPlaceholder),
    v2: build('v2', ssfprnTooltip, ssfprnPlaceholder)
  }
})()

const rktg = tools.cfg.list(
  fields.rktg, 'Résistance KTG', micro.rktgList, {
    tt: `${firstLetters} : ${tools.key('o')} pour résistant, ${tools.key('n')}
pour non résistant et ${tools.key('nc')} pour non connu`,
    ph: '«o» pour résistant, «n» pour non résistant...',
    ms: true,
    sel: select.cap
  }
)

const blse = tools.cfg.list(
  fields.blse, 'Production de BLSE', micro.blseList, {
    tt: `Taper ${tools.key('o')} pour une entérobactérale productrice de BLSE,
${tools.key('n')} pour une non productrice et ${tools.key('nc')} pour non
connu.`,
    ph: '«o» pour oui, «n» pour non...',
    ms: true,
    sel: select.blse
  }
)

const epc = tools.cfg.list(
  fields.epc, 'Production de carbapénémase (EPC)', micro.epcList, {
    tt: `Taper ${tools.key('o')} pour une entérobactérale productrice de
carbapénémase, ${tools.key('n')} pour une non productrice et ${tools.key('nc')}
pour non connu.`,
    ph: '«o» pour oui, «n» pour non...',
    ms: true,
    sel: select.entCar
  }
)

const ppc = tools.cfg.list(
  fields.ppc, 'Production de carbapénémase (PPC)', micro.ppcList, {
    tt: `Taper ${tools.key('o')} pour un <i>P. aeruginosa</i> producteur de
carbapénémase, ${tools.key('n')} pour un non producteur et ${tools.key('nc')}
pour non connu.`,
    ph: '«o» pour oui, «n» pour non...',
    ms: true,
    sel: select.pyoCar
  }
)

const caz = (function () {
  function build (v, tt, ph) {
    return tools.cfg.list(
      fields.caz, 'Résistance à la ceftazidime (CAZ)', micro.cazList[v], {
        tt: tt, ph: ph, ms: true, sel: select.pyo
      }
    )
  }

  return {
    v1: build('v1', sirnTooltip, sirnPlaceholder),
    v2: build('v2', ssfprnTooltip, ssfprnPlaceholder)
  }
})()

const titles = {
  carba: 'Type de carbapénémase',
  carba2: '2ème type de carbapénémase'
}

const epcType = tools.cfg.list(
  fields.epcType, titles.carba, micro.epcTypeList, {
    sel: select.epc
  }
)

const epcType2 = tools.cfg.list(
  fields.epcType2, titles.carba2, micro.epcTypeList, {
    sel: select.epc,
    op: true,
    ov: '(Aucun)'
  }
)

const ppcType = tools.cfg.list(
  fields.ppcType, titles.carba, micro.ppcTypeList, {
    sel: select.ppc
  }
)

const ppcType2 = tools.cfg.list(
  fields.ppcType2, titles.carba2, micro.ppcTypeList, {
    sel: select.ppc,
    op: true,
    ov: '(Aucun)'
  }
)

const otherField = [
  tools.cfg.text(
    fields.microOrgDetail, `Préciser l'espèce identifiée`, {
      ph: 'Nom...',
      tt: `Taper directement le nom de l'espèce.`,
      sel: select.isMoOther
    }
  )
]

const explaur = tools.cfg.list(
  fields.explaurID, 'Identifiant RÉA expl AUR', [], {
    tt: `Rechercher votre identifiant RÉA expl AUR.`,
    ph: `Taper l'identifiant...`,
    op: true,

    ini: function (vm) {
      const s = vm.surveillance
      if (s.modules.reaExplaur == null ||
          s.modules.reaExplaur.stat.val !== 3) {
        this.ignore = (d, sheet) => true
        return
      }
      const uy = units[s.year]

      const list = []
      const values = []
      const finess = s.establishment.finess
      s.modules.reaExplaur.srvs.forEach(
        srv => {
          list.push(srv.uf.ufType)
          const label = srv.labl
          const code = uy.findFromIndex(srv.uf.ufType).code
          const uf = `${srv.uf.name} (${code})`
          srv.epds.forEach(
            (epd, i) => {
              values.push(
                {
                  code: `${finess}-${label}${i + 1}`,
                  value: `Écouvillon ${finess}-${label}${i + 1} / ${uf}`
                }
              )
            }
          )
        }
      )

      this.config.values = values
      this.ignore = (d, sheet) => {
        if (d == null || !select.aur(d, d) ||
            sheet == null || sheet.unitType == null) {
          return true
        }
        const ut = uy.find(sheet.unitType)
        return ut == null || list.indexOf(ut.index) === -1
      }
    }
  }
)

const microOrganisms = (function () {
  function build (mos, resistances, otherFields = []) {
    return {
      type: 'array',
      field: fields.microOrg,
      config: {
        title: 'MICRO-ORGANISME N°',
        fields: [mos, ...otherFields, ...resistances]
      }
    }
  }

  const resistances = {
    v1: [oxa.v1, c3g.v1, flu, gly.v1, car.v1, pan.v1],

    v2: [
      oxa.v2, c3g.v1, flu, rktg, blse, caz.v1, gly.v2, pan.v1,
      car.v1, epc, epcType, epcType2, ppc, ppcType, ppcType2
    ],

    v3: [
      oxa.v2, c3g.v1, flu, rktg, blse, caz.v1, gly.v2, pan.v1, car.v1,
      epc, epcType, epcType2, ppc, ppcType, ppcType2, explaur
    ],

    v4: [
      oxa.v3, c3g.v2, flu, rktg, blse, caz.v2, gly.v3, pan.v2, car.v2,
      epc, epcType, epcType2, ppc, ppcType, ppcType2, explaur
    ]
  }

  return {
    v1: {
      all: build(code.v1.all, resistances.v1),
      noVirus: build(code.v1.noVirus, resistances.v1)
    },
    v2: {
      all: build(code.v1.all, resistances.v2),
      noVirus: build(code.v1.noVirus, resistances.v2)
    },
    v3: {
      all: build(code.v1.all, resistances.v3),
      noVirus: build(code.v1.noVirus, resistances.v3)
    },
    v4: {
      all: build(code.v1.all, resistances.v4),
      noVirus: build(code.v1.noVirus, resistances.v4)
    },
    v5: {
      all: build(code.v2.all, resistances.v4),
      noVirus: build(code.v2.noVirus, resistances.v4)
    },
    v6: {
      all: build(code.v2.all, resistances.v4, otherField),
      noVirus: build(code.v2.noVirus, resistances.v4, otherField)
    }
  }
})()

export default microOrganisms
